function hasFiniteClientPoint(point) {
  return Number.isFinite(point?.x) && Number.isFinite(point?.y);
}

export function screenScaleForTarget(startClient, endClient, targetPx) {
  if (!hasFiniteClientPoint(startClient) || !hasFiniteClientPoint(endClient)) return null;
  if (!Number.isFinite(targetPx) || targetPx <= 0) return null;

  const projectedSpan = Math.hypot(
    endClient.x - startClient.x,
    endClient.y - startClient.y,
  );
  if (!Number.isFinite(projectedSpan) || projectedSpan < 0.001) return null;
  return targetPx / projectedSpan;
}

export function markerConditionPriority(conditionRating, orderedRatings) {
  if (conditionRating == null) return -1;
  const numericRating = Number(conditionRating);
  if (Number.isInteger(numericRating)) {
    return numericRating >= 0 && numericRating < orderedRatings.length ? numericRating : -1;
  }
  const normalizedRating = String(conditionRating).toLowerCase();
  return orderedRatings.findIndex((rating) => rating.toLowerCase() === normalizedRating);
}

function labelPriority(left, right) {
  const forcedDifference = Number(!!right.forceVisible) - Number(!!left.forceVisible);
  if (forcedDifference !== 0) return forcedDifference;

  const priorityDifference = (right.priority || 0) - (left.priority || 0);
  if (priorityDifference !== 0) return priorityDifference;
  return String(left.key).localeCompare(String(right.key));
}

function clamp(number, minimum, maximum) {
  return Math.min(Math.max(number, minimum), maximum);
}

function labelRectangle(entry, viewport, options) {
  const { edgePaddingPx, anchorGapPx } = options;
  const maximumLeft = viewport.width - edgePaddingPx - entry.width;
  const maximumTop = viewport.height - edgePaddingPx - entry.height;
  if (maximumLeft < edgePaddingPx || maximumTop < edgePaddingPx) return null;

  return {
    left: clamp(entry.x - entry.width / 2, edgePaddingPx, maximumLeft),
    top: clamp(entry.y - anchorGapPx - entry.height, edgePaddingPx, maximumTop),
    width: entry.width,
    height: entry.height,
  };
}

function markerIsOnScreen(entry, viewport) {
  return entry.inFrustum !== false
    && Number.isFinite(entry.x)
    && Number.isFinite(entry.y)
    && entry.x >= 0
    && entry.x <= viewport.width
    && entry.y >= 0
    && entry.y <= viewport.height;
}

function rectanglesOverlap(left, right, gapPx) {
  return left.left < right.left + right.width + gapPx
    && left.left + left.width + gapPx > right.left
    && left.top < right.top + right.height + gapPx
    && left.top + left.height + gapPx > right.top;
}

export function layoutMarkerLabels(entries, viewport, suppliedOptions = {}) {
  const options = {
    anchorGapPx: suppliedOptions.anchorGapPx ?? 12,
    collisionGapPx: suppliedOptions.collisionGapPx ?? 4,
    edgePaddingPx: suppliedOptions.edgePaddingPx ?? 4,
  };
  const occupied = [];
  const placements = new Map();

  entries.slice().sort(labelPriority).forEach((entry) => {
    const rectangle = markerIsOnScreen(entry, viewport)
      ? labelRectangle(entry, viewport, options)
      : null;
    const collides = rectangle && occupied.some((placed) => (
      rectanglesOverlap(rectangle, placed, options.collisionGapPx)
    ));
    const visible = !!rectangle && (!!entry.forceVisible || !collides);
    if (visible) occupied.push(rectangle);
    placements.set(entry.key, { key: entry.key, ...rectangle, visible });
  });

  return entries.map((entry) => placements.get(entry.key));
}
